import { Component, Input, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Customers, Drivers, Orders, Trailers } from '../../interfaces';
import { DataServiceService } from '../../services/data-service.service';

import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { AuthService } from '../../services/auth.service';
import { AssignEquipmentComponent, EquipmentAssignmentResult } from '../../equipment/assign-equipment/assign-equipment.component';


interface DisplayedLocation {
  title: string;
  type: 'yard' | 'customer' | 'driver';
  content: Trailers[];
}

interface LocationOption {
  label: string;
  value: string;
}


@Component({
  selector: 'app-trailer-tracker',
  templateUrl: './trailer-tracker.component.html',
  styleUrl: './trailer-tracker.component.scss'
})

export class TrailerTrackerComponent {
  @ViewChild('equipmentAssigner') equipmentAssigner!: AssignEquipmentComponent;

  constructor(private messageService: MessageService,
    private dataServiceService: DataServiceService,
    private authService: AuthService) {}

  @Input() driverCards: any[] = []
  @Input() trailers: Trailers[] = [];
  @Input() orders: Orders[] = [];
  @Input() customers: Customers[] = [];
  @Input() drivers: Drivers[] = [];
  @Input() selectedDate: Date | null = null;
  
  displayDialog = false;
  hasAccess = false;

  selectedDriver: Drivers | null = null;
  selectedTrailer: Trailers | null = null;
  selectedNewLocation: LocationOption | undefined;

  filteredGroups: { label: string; value: Trailers[]; items: Trailers[] }[] = [];
  displayedLocations: DisplayedLocation[] = [];
  locationOptions: LocationOption[] = [];

  expandedTrailers: { [key: number]: boolean } = {};


  ngOnInit() {
    this.loadLocationOptions();

    this.authService.userProfile$.subscribe((profile) => {
      if (profile?.roles?.includes('Dispatcher') || profile?.roles?.includes('Admin')) {
        this.hasAccess = true;
      } else {
        this.hasAccess = false;
      }
    });
  }

  ngOnChanges() {
    // When any input data changes, refresh the displayed locations
    if (this.trailers && this.trailers.length > 0) {
      this.handleDisplayedLocations();
    }
  }

  // Get available locations for a trailer to move to excluding the current location
  getAvailableLocations(currentTrailer: Trailers): LocationOption[] {
    return this.locationOptions
      .filter(location => location.value !== currentTrailer.location_id?.toString())
      .map(location => ({
        label: location.label,
        value: location.value
      }));
  }


  loadLocationOptions() {
    // Load only salt locations (SALT or BOTH types) for the trailer tracker
    this.dataServiceService.getSaltLocations().subscribe({
      next: (locations) => {
        // Map database locations to location options
        this.locationOptions = locations.map((location: any) => ({
          label: location.location_name || location.name,
          value: location.location_id.toString()
        }));

        
        // Now load the displayed locations
        this.handleDisplayedLocations();
      },
      error: (error) => {
        console.error('Failed to load location options:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load location options'
        });
      }
    });
  }


  saveNewLocation() {
    if (!this.selectedTrailer || !this.selectedNewLocation) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a trailer and a new location.'
      });
      return;
    }

    const newLocationId = this.selectedNewLocation.value;

    // Find if the trailer is currently assigned to any driver
    const currentDriverWithTrailer = this.drivers.find(driver =>
      driver.trailer_id === this.selectedTrailer?.trailer_id
    );

    // If assigning to a driver (Driver location)
    const isAssigningToDriver = this.selectedNewLocation?.label === 'Driver';
    if (isAssigningToDriver && this.selectedDriver) {
      // The assign-equipment component will handle any existing assignments automatically
      this.assignToNewDriver();
    } else {
      // Moving to a physical location
      if (currentDriverWithTrailer) {
        // Unassign from current driver first
        this.dataServiceService.updateDriverDetails(currentDriverWithTrailer.driver_id, {
          ...currentDriverWithTrailer,
          trailer_id: null
        }).subscribe(() => {
          // IMMEDIATELY update local drivers array to remove assignment
          const driverIndex = this.drivers.findIndex(d => d.driver_id === currentDriverWithTrailer.driver_id);
          if (driverIndex !== -1) {
            this.drivers[driverIndex] = {
              ...this.drivers[driverIndex],
              trailer_id: null
            };
          }
          
          this.updateTrailerLocationOnly(newLocationId);
        });
      } else {
        // No driver to unassign from, just update location
        this.updateTrailerLocationOnly(newLocationId);
      }
    }
  }

  private assignToNewDriver() {
    if (!this.selectedDriver || !this.selectedTrailer) return;

    // Use the robust assign-equipment logic instead of custom implementation
    this.equipmentAssigner.assignTrailerToDriver(
      this.selectedDriver,
      this.selectedTrailer,
      (result: EquipmentAssignmentResult) => {
        // Success callback - update local drivers array immediately for instant UI update
        
        // Update the new driver assignment
        const newDriverIndex = this.drivers.findIndex(d => d.driver_id === this.selectedDriver!.driver_id);
        if (newDriverIndex !== -1) {
          this.drivers[newDriverIndex] = {
            ...this.drivers[newDriverIndex],
            trailer_id: this.selectedTrailer!.trailer_id
          };
        }

        // If there was a previous driver, remove their assignment
        const previousDriverIndex = this.drivers.findIndex(d => 
          d.driver_id !== this.selectedDriver!.driver_id && 
          d.trailer_id === this.selectedTrailer!.trailer_id
        );
        if (previousDriverIndex !== -1) {
          this.drivers[previousDriverIndex] = {
            ...this.drivers[previousDriverIndex],
            trailer_id: null
          };
        }

        // Update trailer to Driver location
        const driverLocation = this.locationOptions.find(loc => loc.label === 'Driver');
        const driverLocationId = driverLocation ? Number(driverLocation.value) : 0;
        
        this.dataServiceService.updateTrailerLocation(this.selectedTrailer!.trailer_id, {
          ...this.selectedTrailer!,
          location_id: driverLocationId
        }).subscribe({
          next: () => {
            // Refresh data for backend sync
            this.refreshData();
          },
          error: (error) => {
            console.error('Failed to update trailer location:', error);
          }
        });
      },
      (error) => {
        // Error callback - the assign-equipment component already handles error messaging
        console.error('Trailer assignment failed:', error);
      }
    );
  }

  private updateTrailerLocationOnly(newLocationId: string) {
    this.dataServiceService.updateTrailerLocation(this.selectedTrailer!.trailer_id, {
      ...this.selectedTrailer!,
      location_id: Number(newLocationId)
    }).subscribe({
      next: () => {
        // Refresh both drivers and trailers data
        this.refreshData();
      }
    });
  }

  openLocationDialog(trailer: Trailers) {
    this.selectedTrailer = trailer;
    this.displayDialog = true;
  }

  onSelect(event: Trailers) {
    if (event) {
      this.displayedLocations = this.displayedLocations.map(location => ({
        title: location.title,
        type: location.type,
        content: location.content.filter(trailer => trailer.trailer_id === event.trailer_id)
      })).filter(location => location.content.length > 0);
    }
  }


  onClear() {
    this.selectedTrailer = null;
    this.selectedNewLocation = undefined;
    this.handleDisplayedLocations();
  }

  handleDisplayedLocations() {
    // Use reactive streams to get current data
    this.dataServiceService.trailers$.subscribe(trailers => {
      // Filter trailers to include only those with the "SALT" setup (case-insensitive)
      const saltTrailers: Trailers[] = trailers.filter((t: Trailers) =>
        t.setup?.toUpperCase().includes('SALT')
      );

        // Map each locationOption to a group
        const locationGroups = this.locationOptions.map(location => {
          const isDriver = location.label === 'Driver';
          const locationId = Number(location.value);

          const filteredTrailers = saltTrailers.filter(t => {
            // For all locations (including Driver), match the location_id
            return t.location_id === locationId;
          });

          return {
            title: location.label,
            type: isDriver ? 'driver' : 'yard' as 'yard' | 'customer' | 'driver',
            content: filteredTrailers
          };
        });

      this.displayedLocations = locationGroups;
    });
  }

  filterGroupedTrailers(event: AutoCompleteCompleteEvent) {
    let query = event.query.toLowerCase();
    this.filteredGroups = [];

    for (let option of this.displayedLocations) {
      let filteredSubOptions = option.content.filter(item =>
        item.trailer_number.toLowerCase().includes(query)
      );

      if (filteredSubOptions && filteredSubOptions.length) {
        this.filteredGroups.push({
          label: option.title,
          value: filteredSubOptions,
          items: filteredSubOptions
        });
      }
    }

    if (query) {
      const newLocations = this.displayedLocations.map(location => {
        const filteredContent = location.content?.filter(trailer =>
          trailer.trailer_number.toLowerCase().includes(query)
        ) || [];

        return {
          title: location.title,
          type: location.type as 'yard' | 'customer' | 'driver',
          content: filteredContent
        };
      }).filter(location => location.content.length > 0);

      this.displayedLocations = newLocations;
    } else {
      // Handle resetting to original locations
      this.handleDisplayedLocations();
    }
  }

  
  getDriverNameByTrailerId(trailer_id: number): string {
    const driver = this.drivers.find(driver => driver.trailer_id === trailer_id);
    return driver ? `${driver.first_name} ${driver.last_name}` : 'Unknown Driver';
  }

  isTrailerAtDriverLocation(trailer: Trailers): boolean {
    // Find the Driver location from locationOptions
    const driverLocation = this.locationOptions.find(loc => loc.label === 'Driver');
    if (driverLocation) {
      return trailer.location_id === Number(driverLocation.value);
    }
    return false;
  }

  reset() {
    this.selectedNewLocation = undefined
  }



  toggleTrailerDetails(trailer: Trailers) {
    this.expandedTrailers[trailer.trailer_id] = !this.expandedTrailers[trailer.trailer_id];
  }

  // Helper method to get the most relevant order for a trailer based on selected date
  private getRelevantOrderForTrailer(trailer: Trailers): Orders | undefined {
    if (!trailer.trailer_id) return undefined;
    
    // Get all orders for this trailer
    const trailerOrders = this.orders.filter(o => o.trailer_id === trailer.trailer_id);
    
    if (trailerOrders.length === 0) return undefined;
    
    // If we have a selected date, filter orders for that date
    if (this.selectedDate) {
      const selectedDateStr = this.selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const relevantOrders = trailerOrders.filter(order => {
        // Check if order's pickup_date or delivery_date matches selected date
        const pickupDate = order.pickup_date ? new Date(order.pickup_date).toISOString().split('T')[0] : null;
        const deliveryDate = order.delivery_date ? new Date(order.delivery_date).toISOString().split('T')[0] : null;
        const dateReceived = order.date_received ? new Date(order.date_received).toISOString().split('T')[0] : null;
        
        return pickupDate === selectedDateStr || 
               deliveryDate === selectedDateStr || 
               dateReceived === selectedDateStr ||
               order.load_status === 'Loaded';
      });
      
      if (relevantOrders.length > 0) {
        // Prioritize loaded orders first, then by most recent order_id
        return relevantOrders.sort((a, b) => {
          // Loaded orders get priority
          if (a.load_status === 'Loaded' && b.load_status !== 'Loaded') return -1;
          if (b.load_status === 'Loaded' && a.load_status !== 'Loaded') return 1;
          // Then sort by order_id (most recent first)
          return b.order_id - a.order_id;
        })[0];
      }
    }
    
    // Fallback: return the most recent loaded order, or most recent order overall
    const loadedOrders = trailerOrders.filter(o => o.load_status === 'Loaded');
    if (loadedOrders.length > 0) {
      return loadedOrders.sort((a, b) => b.order_id - a.order_id)[0];
    }
    
    return trailerOrders.sort((a, b) => b.order_id - a.order_id)[0];
  }


  getTrailerLoadStatus(trailer: Trailers): string {
    // Find the most relevant order for this trailer
    const order = this.getRelevantOrderForTrailer(trailer);
    if (order && order.load_status === 'Loaded') {
      return 'Loaded';
    }
    return 'Empty';
  }


  isTrailerLoaded(trailer: Trailers): boolean {
    const order = this.getRelevantOrderForTrailer(trailer);
    return order?.load_status === 'Loaded';
  }

  getCustomerName(trailer: Trailers): string {
    const order = this.getRelevantOrderForTrailer(trailer);
    if (order) {
      // Find customer by receiver_id (assuming this is the customer we want to show)
      const customer = this.customers.find(c => c.customer_id === order.receiver_id);
      return customer ? customer.name : 'Unknown Customer';
    }
    return 'No Customer';
  }

  getPickupNumber(trailer: Trailers): string {
    const order = this.getRelevantOrderForTrailer(trailer);
    return order?.pickup_number || '';
  }

  // Check if trailer has Higrade or Sea Salt status when loaded
  shouldShowSpecialStatusPill(trailer: Trailers): boolean {
    const isLoaded = this.isTrailerLoaded(trailer);
    const hasSpecialStatus = trailer.status === 'Higrade' || trailer.status === 'Sea Salt';
    return isLoaded && hasSpecialStatus;
  }

  // Get the special status for the pill
  getSpecialStatus(trailer: Trailers): string {
    return trailer.status || '';
  }

  private refreshData(): void {
    // Trigger refresh by calling the API methods which will update the reactive streams
    this.dataServiceService.getAllDrivers().subscribe();
    this.dataServiceService.getAllTrailers().subscribe();
    this.dataServiceService.getOrders().subscribe();
    
    // Reset dialog state
    this.displayDialog = false;
    this.selectedNewLocation = undefined;
    this.selectedDriver = null;
    
  }
}